import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, withModifiers as _withModifiers, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["draggable"]
const _hoisted_2 = { key: 1 }
const _hoisted_3 = {
  key: 0,
  class: "node-header"
}
const _hoisted_4 = {
  key: 1,
  class: "children"
}
const _hoisted_5 = ["onDragover"]

import { ref, computed, onMounted } from 'vue'
import { useDisplay } from 'vuetify'
import { useTabsStore } from '@/stores/tabs'
import type { TreeNode as TreeNodeType } from '@/types/editor'


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'TreeNodeComponent' },
  __name: 'TreeNode',
  props: {
    node: {},
    simplify: { type: Boolean }
  },
  emits: ["rightclick"],
  setup(__props: any, { emit: __emit }) {

/**
 * TreeNodeComponent — recursive ASN.1 tree node.
 *
 * We use `defineOptions({ name: 'TreeNodeComponent' })` so the recursive
 * `<TreeNodeComponent>` reference in the template resolves correctly.
 */


const { mobile: isMobile } = useDisplay()
const store = useTabsStore()

const props = __props

const emit = __emit

const expandedContent = ref(false)

// Refs for DOM elements (kept for potential future use)
const tagRef = ref<HTMLElement | null>(null)
const labelRef = ref<HTMLElement | null>(null)
const lengthRef = ref<HTMLElement | null>(null)
const contentRef = ref<HTMLElement | null>(null)

const showDropZones = computed(() => store.isDragging)

const hasChildren = computed(() => {
  return props.node.children && props.node.children.length > 0
})

const isConstructed = computed(() => {
  return props.node.tag[0] === 48 || props.node.tag[0] === 49
})

const isHighlighted = computed(() => store.highlighted === props.node.id) //Highlighting Hex -> Tree
const isLocked = computed(() => store.locked === props.node.id)
const isSubHighlighted = computed(() =>
  store.locked !== -1 &&
  store.locked !== props.node.id &&
  store.highlighted === props.node.id &&
  store.isDescendant(store.locked, props.node.id)
)

const isExpanded = computed(() => store.isExpanded(props.node.id))

onMounted(() => {
  if (props.node.children.length < 2) {
    toggleExpand(true)
  }
})

function openMenu(event: MouseEvent | TouchEvent): void {
  event.preventDefault()
  const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX
  const clientY = 'touches' in event ? event.touches[0].clientY : event.clientY
  emit('rightclick', clientX, clientY, props.node.id)
}

function handleTouchStart(_event: TouchEvent): void {
  // placeholder for long-press context menu on mobile
}

function handleTouchEnd(_event: TouchEvent): void {
  // placeholder
}

function onHeaderClick(event: MouseEvent): void {
  event.stopPropagation() // keeps this click from reaching any future "click outside" listener
  store.elementLocked(store.locked === props.node.id ? -1 : props.node.id)
}

function onDragStart(event: DragEvent): void {
  event.dataTransfer!.effectAllowed = 'move'
  ;(event.target as HTMLElement).style.opacity = '0.4'
  store.draggingSet(true)
  store.draggedNodeIdSet(props.node.id)
}

function onDragEnd(event: DragEvent): void {
  ;(event.target as HTMLElement).style.opacity = ''
  const t = store.target
  const draggedId = store.draggedNodeId

  if (
    t[0] !== -1 &&
    t[0] !== props.node.id &&
    !store.isDescendant(draggedId!, t[0])
  ) {
    store.nodeMoved({
      tab: store.currentTab,
      id: props.node.id,
      target: t[0],
      index: t[1]
    })
  }

  store.dragTargetSet([-1, -1])
  store.draggingSet(false)
  store.draggedNodeIdSet(null)
  store.activeDropContextSet(null)
}

function onDragOver(_event: DragEvent, index: number, isDropZone = false): void {
  const parentId = props.node.parent
  store.activeDropContextSet(isDropZone ? props.node.id : parentId ?? props.node.id)
  store.dragTargetSet([props.node.id, index])
}

function onDragLeave(): void {
  store.dragTargetSet([-1, -1])
}

function isDragOverSelf(index: number): boolean {
  const t = store.target
  return t[0] === props.node.id && t[1] === index
}

function toggleExpand(value: boolean | null = null): void {
  store.expandedSet({
    id: props.node.id,
    expanded: value !== null ? value : !isExpanded.value
  })
}

return (_ctx: any,_cache: any) => {
  const _component_TreeNodeComponent = _resolveComponent("TreeNodeComponent")!

  return (_openBlock(), _createElementBlock("div", {
    draggable: !_unref(isMobile),
    class: "tree-node draggable",
    onContextmenu: _withModifiers(openMenu, ["stop"]),
    onTouchstart: _withModifiers(handleTouchStart, ["stop"]),
    onTouchend: _withModifiers(handleTouchEnd, ["stop"]),
    onMouseover: _cache[6] || (_cache[6] = _withModifiers(($event: any) => (_unref(store).elementHighlighted(__props.node.id)), ["stop"])),
    onMouseleave: _cache[7] || (_cache[7] = _withModifiers(($event: any) => (_unref(store).elementHighlighted(-1)), ["stop"])),
    onDragstart: _withModifiers(onDragStart, ["stop"]),
    onDragend: _withModifiers(onDragEnd, ["stop"])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["node-header", { 
        dragover: isDragOverSelf(__props.node.children.length), 
        modified: __props.node.edited, 
        highlighted: isHighlighted.value && !isLocked.value,
        locked: isLocked.value,
        'sub-highlighted': isSubHighlighted.value
        }]),
      onClick: onHeaderClick,
      onDragover: _cache[2] || (_cache[2] = _withModifiers((event) => onDragOver(event, __props.node.children.length), ["prevent"])),
      onDragleave: onDragLeave
    }, [
      (hasChildren.value || isConstructed.value)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            class: "toggle-icon",
            onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (toggleExpand()), ["stop"]))
          }, _toDisplayString(isExpanded.value ? '▼' : '▶'), 1))
        : _createCommentVNode("", true),
      (__props.node.tag && !__props.simplify)
        ? (_openBlock(), _createElementBlock("span", {
            key: 1,
            class: "node-tag tag",
            ref_key: "tagRef",
            ref: tagRef
          }, _toDisplayString(__props.node.tag[1]), 513))
        : _createCommentVNode("", true),
      (__props.node.label)
        ? (_openBlock(), _createElementBlock("span", {
            key: 2,
            class: "node-label label",
            ref_key: "labelRef",
            ref: labelRef
          }, _toDisplayString(__props.node.label), 513))
        : _createCommentVNode("", true),
      (__props.node.length && !__props.simplify)
        ? (_openBlock(), _createElementBlock("span", {
            key: 3,
            class: "node-length length",
            ref_key: "lengthRef",
            ref: lengthRef
          }, _toDisplayString(__props.node.length[1]), 513))
        : _createCommentVNode("", true),
      (__props.node.content && !expandedContent.value)
        ? (_openBlock(), _createElementBlock("span", {
            key: 4,
            class: "node-content content",
            ref_key: "contentRef",
            ref: contentRef
          }, [
            (__props.node.content[1].length > 40)
              ? (_openBlock(), _createElementBlock("span", {
                  key: 0,
                  onClick: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (expandedContent.value = true), ["stop"]))
                }, _toDisplayString(__props.node.content[1].slice(0, 20)) + "..." + _toDisplayString(__props.node.content[1].slice(-20)), 1))
              : (_openBlock(), _createElementBlock("span", _hoisted_2, _toDisplayString(__props.node.content[1]), 1))
          ], 512))
        : _createCommentVNode("", true)
    ], 34),
    (__props.node.content && expandedContent.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          _createElementVNode("span", {
            class: "node-content",
            ref_key: "contentRef",
            ref: contentRef,
            onClick: _cache[3] || (_cache[3] = _withModifiers(($event: any) => (expandedContent.value = false), ["stop"]))
          }, _toDisplayString(__props.node.content[1]), 513)
        ]))
      : _createCommentVNode("", true),
    (isExpanded.value && (hasChildren.value || isConstructed.value))
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          (showDropZones.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: _normalizeClass(["drop-zone", { 'dragover-active': isDragOverSelf(0) }]),
                onDragover: _cache[4] || (_cache[4] = _withModifiers((event) => onDragOver(event, 0), ["prevent"])),
                onDragleave: onDragLeave
              }, null, 34))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.node.children, (child, index) => {
            return (_openBlock(), _createElementBlock("div", { key: child }, [
              _createVNode(_component_TreeNodeComponent, {
                node: _unref(store).getNodeFromId(child),
                simplify: __props.simplify,
                onRightclick: _cache[5] || (_cache[5] = (x, y, id) => emit('rightclick', x, y, id))
              }, null, 8, ["node", "simplify"]),
              (showDropZones.value && (index < __props.node.children.length - 1 || isDragOverSelf(__props.node.children.length)))
                ? (_openBlock(), _createElementBlock("div", {
                    key: 0,
                    class: _normalizeClass(["drop-zone", { 'dragover-active': isDragOverSelf(index + 1) }]),
                    onDragover: _withModifiers((event) => onDragOver(event, index + 1, true), ["prevent"]),
                    onDragleave: onDragLeave
                  }, null, 42, _hoisted_5))
                : _createCommentVNode("", true)
            ]))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ], 40, _hoisted_1))
}
}

})